export type StringURLParse = {
  type: 'Fragment' | 'URL';
  value: string;
};

function dedupe(interleavedParts: StringURLParse[]): StringURLParse[] {
  // perform consec fragment joins
  const finalParts: StringURLParse[] = [];
  let buf: string[] = [];
  for (const part of interleavedParts) {
    if (part.type === 'Fragment') {
      buf.push(part.value);
    } else if (part.type === 'URL') {
      const aggregateFragment = buf.join('');
      if (aggregateFragment.length > 0) {
        finalParts.push({
          type: 'Fragment',
          value: aggregateFragment
        });
        buf = [];
      }
      finalParts.push(part);
    }
  }
  const aggregateFragment = buf.join('');
  if (aggregateFragment.length > 0) {
    finalParts.push({
      type: 'Fragment',
      value: aggregateFragment
    });
    buf = [];
  }

  return finalParts;
}

// Order matters: links and **bold** before *italic*.
const MARKDOWN_TO_PROTECT = [
  /\[[^\]]*\]\(https?:\/\/[^\s)]+\)/,
  /\[[^\]]*\]\(cai-cta:\/\/[^\s)]+\)/,
  /\*\*[^*]+\*\*/,
  /\*[^*]+\*/,
  /_[^_]+_/,
] as const;

function protectMarkdown(text: string) {
  const stash: string[] = [];
  let protectedText = text;

  for (const pattern of MARKDOWN_TO_PROTECT) {
    protectedText = protectedText.replace(new RegExp(pattern.source, 'g'), (match) => {
      const placeholder = `<<MD${stash.push(match) - 1}>>`;
      return placeholder;
    });
  }

  const unstash = (value: string) =>
    value.replace(/<<MD(\d+)>>/g, (_, index) => stash[Number(index)] ?? `<<MD${index}>>`);

  return { protectedText, unstash };
}

function isUrlToken(
  token: string,
  urlPattern: RegExp,
  simpleUrlPattern: RegExp,
  markdownLinkPattern: RegExp,
  delimitedUrlRegex: RegExp
) {
  const isMajorTokenURL = urlPattern.test(token) || simpleUrlPattern.test(token);
  const isMarkdownLink = markdownLinkPattern.test(token);
  const isTokenDelimitedURL = delimitedUrlRegex.test(token);

  return {
    isUrl: isMajorTokenURL || isMarkdownLink || isTokenDelimitedURL,
    value: isTokenDelimitedURL ? token.replace(/</g, '').replace(/>(?=[^>]*$)/, '') : token,
  };
}

export function parseStringForURLs(body: string): StringURLParse[] {
  // start by decoding any HTML encoded characters
  body = decodeEntities(body);

  const whiteSpaceSplit = /\s/g;
  const urlPattern = /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/;
  const simpleUrlPattern = /^https?:\/\/\S+$/i;
  const markdownLinkPattern = /^\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)$/;
  const delimitedUrlRegex = /(?:<|&lt;)(https?:\/\/[^>\s]+)(?:>|&gt;)/;

  const { protectedText, unstash } = protectMarkdown(body);

  const residualParts = protectedText.match(whiteSpaceSplit) || [];
  const majorTokens = protectedText.split(whiteSpaceSplit);

  if (majorTokens.length !== residualParts.length + 1) {
    return [];
  }
  const interleavedParts: StringURLParse[] = [];

  for (let i = 0; i < residualParts.length; i++) {
    const token = unstash(majorTokens[i] || '');
    const { isUrl, value } = isUrlToken(token, urlPattern, simpleUrlPattern, markdownLinkPattern, delimitedUrlRegex);

    interleavedParts.push({
      type: isUrl ? 'URL' : 'Fragment',
      value,
    });

    interleavedParts.push({
      type: 'Fragment',
      value: residualParts[i] || '',
    });
  }

  const lastToken = unstash(majorTokens[majorTokens.length - 1] || '');
  const { isUrl, value } = isUrlToken(lastToken, urlPattern, simpleUrlPattern, markdownLinkPattern, delimitedUrlRegex);

  interleavedParts.push({
    type: isUrl ? 'URL' : 'Fragment',
    value,
  });

  return dedupe(interleavedParts).map((part) => ({
    ...part,
    value: unstash(part.value),
  }));
}

/**
 * Return file extension from filename. Can handle files with no extension or files starting with a dot.
 *
 * @param fileName Filename which contains the extension
 *
 * @return string Extension string
 */
export function getFileExtension(fileName: string) {
  return fileName.slice((Math.max(0, fileName.lastIndexOf(".")) || Infinity) + 1);
}

export function splitMarkdownLink(markdownLink: string): { linkText: string; url: string } | null {
  const markdownLinkPattern = /^\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)$/;
  const match = markdownLink.match(markdownLinkPattern);

  if (match) {
    return {
      linkText: match[1]!,
      url: match[2]!
    };
  }

  return null;
}

export function decodeEntities(htmlString: string) {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = htmlString;
  return textarea.value;
}

